import { useCompareData as useSharedCompareData } from "@repo/shared/hooks";
import { comparePlayersSelector } from "@repo/shared/redux";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router";

/**
 * The web app's URL adapter over `@repo/shared/hooks`' `useCompareData` — the
 * data flow itself, the graded role and its label all live there. All this
 * owns is `?resourceIds=`, which react-router can't be read from inside the
 * shared package.
 *
 * `syncUrl` enables the routed-page behavior: the URL ids seed the first fetch
 * (handled by the shared hook), players staged before mount get mirrored out
 * once, and every successful fetch/remove rewrites the query. The embedded
 * sheet passes false and leaves the URL alone in both directions.
 */
export const useCompareData = ({ syncUrl }: { syncUrl: boolean }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const players = useSelector(comparePlayersSelector);
  const writeIdsToUrl = (ids: number[]) => {
    const next = new URLSearchParams(searchParams);
    if (ids.length) next.set("resourceIds", ids.join(","));
    else next.delete("resourceIds");
    setSearchParams(next, { replace: true });
  };

  // Mirror-out only: players staged before the routed page mounts get their
  // ids written to the URL once, so the link is shareable straight away.
  const mirrored = useRef(false);
  useEffect(() => {
    if (!syncUrl || mirrored.current || players.length === 0) return;
    mirrored.current = true;
    writeIdsToUrl(players.map((p) => p.resourceId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncUrl]);

  return useSharedCompareData({
    seedIdsParam: syncUrl ? searchParams.get("resourceIds") : null,
    onIdsChange: (ids) => {
      if (syncUrl) writeIdsToUrl(ids);
    },
  });
};
